import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Play, Square, Loader2, Save, Phone, PhoneOff } from 'lucide-react';
import { apiRequest } from '@/lib/api';

// ─── Voice Data ───

const VOICES = [
  { value: 'marin', label: 'Marin', gender: 'Female', popular: true },
  { value: 'cedar', label: 'Cedar', gender: 'Male', popular: true },
  { value: 'alloy', label: 'Alloy', gender: 'Male', popular: false },
  { value: 'ash', label: 'Ash', gender: 'Male', popular: false },
  { value: 'coral', label: 'Coral', gender: 'Female', popular: false },
  { value: 'sage', label: 'Sage', gender: 'Female', popular: false },
  { value: 'shimmer', label: 'Shimmer', gender: 'Female', popular: false },
  { value: 'ballad', label: 'Ballad', gender: 'Male', popular: false },
  { value: 'echo', label: 'Echo', gender: 'Male', popular: false },
  { value: 'verse', label: 'Verse', gender: 'Male', popular: false },
];

const VOICE_PREVIEWS = new Set([
  'alloy', 'ash', 'ballad', 'cedar', 'coral', 'echo', 'fable',
  'marin', 'nova', 'onyx', 'sage', 'shimmer', 'verse',
]);

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'he', label: 'Hebrew' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
];

// ─── Types ───

interface AgentData {
  id: string;
  name: string;
  voice: string;
  language: string;
  company_name: string;
  greeting_text: string;
  notification_email: string;
}

// ─── Component ───

export function MyAgentPage() {
  const [agents, setAgents] = useState<AgentData[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [phoneMap, setPhoneMap] = useState<Record<string, string>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    loadAgents();
    loadPhoneNumbers();
  }, []);

  const loadPhoneNumbers = async () => {
    try {
      const data = await apiRequest<{ numbers: { number: string; agent_id: string }[] }>('/billing/my-numbers');
      if (data.numbers) {
        const map: Record<string, string> = {};
        for (const n of data.numbers) {
          if (n.agent_id) map[n.agent_id] = n.number;
        }
        setPhoneMap(map);
      }
    } catch {
      // No phone numbers — that's fine
    }
  };

  const loadAgents = async () => {
    try {
      const data = await apiRequest<{ agents: AgentData[] }>('/studio/agents');
      if (data.agents && data.agents.length > 0) {
        const mapped = data.agents.map((a) => ({
          id: a.id,
          name: a.name || '',
          voice: a.voice || 'marin',
          language: a.language || 'en',
          company_name: a.company_name || '',
          greeting_text: a.greeting_text || '',
          notification_email: a.notification_email || '',
        }));
        setAgents(mapped);
        setSelectedId(mapped[0].id);
        setAgent(mapped[0]);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const selectAgent = (id: string) => {
    const found = agents.find((a) => a.id === id);
    if (found) {
      setSelectedId(id);
      setAgent({ ...found });
      setSaved(false);
    }
  };

  const handleSave = async () => {
    if (!agent) return;
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await apiRequest(`/studio/agents/${agent.id}`, {
        method: 'PATCH',
        body: {
          name: agent.name,
          voice: agent.voice,
          language: agent.language,
          company_name: agent.company_name,
          greeting_text: agent.greeting_text,
          notification_email: agent.notification_email,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const togglePlay = (voiceName: string) => {
    if (playingVoice === voiceName) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingVoice(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(`/audio/voices/${voiceName}.mp3`);
    audio.onended = () => setPlayingVoice(null);
    audio.play();
    audioRef.current = audio;
    setPlayingVoice(voiceName);
  };

  const update = (field: keyof AgentData, value: string) => {
    if (!agent) return;
    setAgent({ ...agent, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-foreground">My Agent</h1>
        <p className="mt-4 text-muted-foreground">
          Your agent is being set up. You'll be able to configure it here once it's ready.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Agent</h1>
            <p className="text-sm text-muted-foreground">Customize how your AI agent sounds and behaves</p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : saved ? (
              <Save className="mr-2 h-4 w-4 text-green-500" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {saved ? 'Saved' : 'Save Changes'}
          </Button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
            <button className="ml-2 underline" onClick={() => setError('')}>dismiss</button>
          </div>
        )}

        <div className="space-y-6">
          {/* Agent Selector */}
          {agents.length > 1 && (
            <div>
              <Label>Agent</Label>
              <Select value={selectedId || ''} onValueChange={selectAgent}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name || 'Unnamed Agent'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Agent Name */}
          <div>
            <Label>Agent Name</Label>
            <Input
              className="mt-1.5"
              value={agent.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. Sarah"
            />
          </div>

          {/* Company Name */}
          <div>
            <Label>Company Name</Label>
            <Input
              className="mt-1.5"
              value={agent.company_name}
              onChange={(e) => update('company_name', e.target.value)}
              placeholder="e.g. Acme Inc"
            />
          </div>

          {/* Voice */}
          <div>
            <Label>Voice</Label>
            <div className="mt-1.5 flex items-center gap-2">
              {VOICE_PREVIEWS.has(agent.voice) && (
                <button
                  type="button"
                  onClick={() => togglePlay(agent.voice)}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors ${
                    playingVoice === agent.voice
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:bg-primary/10 hover:text-primary'
                  }`}
                  title={playingVoice === agent.voice ? 'Stop preview' : 'Preview voice'}
                >
                  {playingVoice === agent.voice ? (
                    <Square className="h-3.5 w-3.5 fill-current" />
                  ) : (
                    <Play className="h-3.5 w-3.5 fill-current" />
                  )}
                </button>
              )}
              <Select value={agent.voice} onValueChange={(v) => update('voice', v)}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VOICES.map((v) => (
                    <SelectItem key={v.value} value={v.value}>
                      <span className="flex items-center gap-2">
                        {v.label} ({v.gender})
                        {v.popular && (
                          <span className="text-[10px] font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">Popular</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Language */}
          <div>
            <Label>Primary Language</Label>
            <Select value={agent.language} onValueChange={(v) => update('language', v)}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Greeting */}
          <div>
            <Label>Greeting</Label>
            <Textarea
              className="mt-1.5"
              value={agent.greeting_text}
              onChange={(e) => update('greeting_text', e.target.value)}
              rows={3}
              placeholder="What your agent says when answering a call"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Leave empty to use the default greeting
            </p>
          </div>

          {/* Notification Email */}
          <div>
            <Label>Notification Email</Label>
            <Input
              className="mt-1.5"
              type="email"
              value={agent.notification_email}
              onChange={(e) => update('notification_email', e.target.value)}
              placeholder="Where to send call summaries"
            />
          </div>
        </div>

        {/* Phone Number + Call Forwarding — only for agents with an assigned number */}
        {agent && phoneMap[agent.id] && (() => {
          const num = phoneMap[agent.id];
          const cleanNum = num.replace(/[^+\d]/g, '');
          return (
            <div className="mt-8 pt-8 border-t">
              <h2 className="text-lg font-semibold text-foreground mb-1">Your Phone Number</h2>
              <p className="text-lg font-mono text-foreground mb-4">{num}</p>

              <p className="text-sm text-muted-foreground mb-4">
                Set up call forwarding so your AI agent answers when you can't.
              </p>

              <div className="flex gap-3">
                <a
                  href={`tel:**004*${cleanNum}%23`}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Activate Forwarding
                </a>
                <a
                  href="tel:%23%23004%23"
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <PhoneOff className="h-4 w-4" />
                  Cancel Forwarding
                </a>
              </div>

              <div className="mt-3 space-y-1">
                <p className="text-xs text-muted-foreground">
                  Activate: dial <span className="font-mono">**004*{cleanNum}#</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Cancel: dial <span className="font-mono">##004#</span>
                </p>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
